import React, { useState, useEffect, Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import Logo from '../../images/gattomioNero.png';
import { getOrderById, trackOrder } from '../../api/backend';

const statusConfig = {
  pending: { label: 'In attesa', badgeClass: 'status-pending' },
  processing: { label: 'In elaborazione', badgeClass: 'status-processing' },
  completed: { label: 'Completato', badgeClass: 'status-completed' },
  shipped: { label: 'Spedito', badgeClass: 'status-shipped' },
  cancelled: { label: 'Annullato', badgeClass: 'status-cancelled' },
};

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [orderData, trackData] = await Promise.all([
          getOrderById(orderId),
          trackOrder(orderId),
        ]);
        setOrder(orderData);
        setTimeline(trackData.timeline || []);
      } catch (err) {
        if (err?.message === 'Non autorizzato ad accedere a questo ordine') {
          setError('Non sei autorizzato a vedere questo ordine.');
        } else {
          setError('Ordine non trovato o errore di caricamento.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orderId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return '€ ' + parseFloat(amount || 0).toFixed(2).replace('.', ',');
  };

  const getOrderNumber = (id) => `ORD-${String(id).padStart(6, '0')}`;

  if (loading) {
    return (
      <Fragment>
        <Navbar hclass={'wpo-site-header'} Logo={Logo} />
        <PageTitle pageTitle={'Dettaglio ordine'} pagesub={'Ordini'} />
        <section className="wpo-orders-section section-padding">
          <div className="container">
            <div className="orders-loading">
              <div className="spinner">
                <div className="spinner-border" style={{ color: '#FF5B2E', width: '40px', height: '40px' }} role="status">
                  <span className="visually-hidden">Caricamento...</span>
                </div>
              </div>
              <p>Caricamento dettagli ordine...</p>
            </div>
          </div>
        </section>
        <Footer />
        <Scrollbar />
      </Fragment>
    );
  }

  if (error) {
    return (
      <Fragment>
        <Navbar hclass={'wpo-site-header'} Logo={Logo} />
        <PageTitle pageTitle={'Dettaglio ordine'} pagesub={'Ordini'} />
        <section className="wpo-orders-section section-padding">
          <div className="container">
            <div className="order-detail-error">
              <div className="error-alert">{error}</div>
              <Link to="/orders" className="error-btn">Torna ai miei ordini</Link>
            </div>
          </div>
        </section>
        <Footer />
        <Scrollbar />
      </Fragment>
    );
  }

  const status = statusConfig[order?.status] || { label: order?.status, badgeClass: 'status-pending' };

  return (
    <Fragment>
      <Navbar hclass={'wpo-site-header'} Logo={Logo} />
      <PageTitle pageTitle={'Dettaglio ordine'} pagesub={'Ordini'} />

      <section className="wpo-orders-section section-padding">
        <div className="container">

          {/* Header ordine */}
          <div className="order-detail-header">
            <div className="header-info">
              <h3>{getOrderNumber(order.id)}</h3>
              <small>Effettuato il {formatDate(order.created_at)}</small>
            </div>
            <div className="header-actions">
              <span className={`status-badge ${status.badgeClass}`}>{status.label}</span>
              <Link to="/orders" className="back-btn">← I miei ordini</Link>
            </div>
          </div>

          <div className="order-detail-wrapper">

            {/* Timeline */}
            <div className="order-detail-timeline">
              <div className="detail-card">
                <div className="card-header">
                  <h4>Tracking ordine</h4>
                </div>
                <div className="card-body">
                  {timeline.map((step, i) => (
                    <div key={i} className="timeline-item">
                      <div className="timeline-icon">
                        <span>{step.icon}</span>
                        {i < timeline.length - 1 && (
                          <div className={`timeline-line${step.status === 'completed' ? ' completed' : ''}`}></div>
                        )}
                      </div>
                      <div className="timeline-content">
                        <p className={step.status === 'pending' ? 'text-muted' : ''}>
                          <strong>{step.step}</strong>
                        </p>
                        {step.date && (
                          <small>{formatDate(step.date)}</small>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Colonna destra: prodotti + info */}
            <div className="order-detail-products">

              {/* Prodotti */}
              <div className="products-card">
                <div className="card-header">
                  <h4>Prodotti ordinati</h4>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table>
                      <thead>
                        <tr>
                          <th>Prodotto</th>
                          <th className="text-center">Qtà</th>
                          <th className="text-end">Prezzo unitario</th>
                          <th className="text-end">Totale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.order_products?.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <strong>{item.product_name}</strong>
                              {item.product_sku && (
                                <div><small>SKU: {item.product_sku}</small></div>
                              )}
                            </td>
                            <td className="text-center">{item.quantity}</td>
                            <td className="text-end">{formatAmount(item.unit_price)}</td>
                            <td className="text-end"><strong>{formatAmount(item.total_price)}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="3" className="text-end">Totale ordine:</td>
                          <td className="text-end">
                            <strong className="text-primary">{formatAmount(order.amount)}</strong>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>

              {/* Dati cliente, spedizione, pagamento */}
              <div className="order-detail-info">
                <div className="info-card">
                  <div className="card-header">
                    <h4>Dati cliente</h4>
                  </div>
                  <div className="card-body">
                    <p><strong>{order.client_name}</strong></p>
                    <p className="text-muted">{order.client_email}</p>
                    {order.client_phone && <p className="text-muted">{order.client_phone}</p>}
                  </div>
                </div>
                <div className="info-card">
                  <div className="card-header">
                    <h4>Indirizzo di spedizione</h4>
                  </div>
                  <div className="card-body">
                    <p>{order.shipping_address}</p>
                    <p>{order.shipping_zip} {order.shipping_city}</p>
                    <p>{order.shipping_country}</p>
                  </div>
                </div>
                <div className="info-card full-width">
                  <div className="card-header">
                    <h4>Pagamento</h4>
                  </div>
                  <div className="card-body">
                    <p>Metodo: <strong>
                      {order.payment_method === 'stripe' ? 'Carta di credito (Stripe)' : 'PayPal'}
                    </strong></p>
                    <p className="text-muted">
                      <small>
                        {order.payment_method === 'stripe'
                          ? `ID transazione: ${order.stripe_payment_intent_id}`
                          : `ID PayPal: ${order.paypal_order_id}`
                        }
                      </small>
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>

      <Footer />
      <Scrollbar />
    </Fragment>
  );
};

export default OrderDetailPage;
